import { memo } from 'react';
import classNames from 'classnames';

import { ApprovalsListResponse } from '@Types/api/approvalsList';
import { MAvatar, MButton } from '@Components/Base';

import { ReactComponent as DatePickerIcon } from '@Icons/additional/date-picker.svg';

import { parseDateTime } from '@Utils/transformDateTime';
import { User } from '@Types/index';
import { ApprovalActions } from '../components';
import { ApprovalHistoryParams } from '../Approvals';

import commonStyle from '../../common-desktop-styles.module.css';
import styles from './component-styles.module.css';

interface Params {
  taskId: string;
  requestId: number;
  requestNo: string | undefined;
  requestTypeId?: string;
}

interface ApprovalsDesktopProps {
  essListCounter: number;
  mrListCounter: number;
  activeList: ApprovalsListResponse[];
  userCollection: Map<string, User>;
  handleGoToApprovals: () => void;
  handleESSApprovals: () => void;
  handleMRApprovals: () => void;
  handleApprovalHistory: ({
    requestId,
    requestService,
    requestTypeId,
  }: ApprovalHistoryParams) => void;
  handleApproveTask: ({
    taskId,
    requestId,
    requestTypeId,
    requestNo,
  }: Params) => void;
  handleRejectTask: ({
    taskId,
    requestId,
    requestTypeId,
    requestNo,
  }: Params) => void;
}

const DATE_FORMAT = 'DD/MM/YYYY [at] hh:mm A';

const ApprovalsDesktop: React.FC<ApprovalsDesktopProps> = ({
  essListCounter = 0,
  mrListCounter = 0,
  activeList,
  userCollection,
  handleGoToApprovals,
  handleESSApprovals,
  handleMRApprovals,
  handleApprovalHistory,
  handleApproveTask,
  handleRejectTask,
}) => {
  return (
    <div
      className={classNames(
        commonStyle.infoBlock,
        'trojan-glassphormism-box trojan-glassphormism-box-shadow'
      )}
    >
      <div className={commonStyle.header}>
        <div className={commonStyle.caption}>My Approvals</div>
        <div className={commonStyle.link} onClick={handleGoToApprovals}>
          Go to Approvals
        </div>
      </div>
      <div className={styles.approvals}>
        <div className={styles.actions}>
          <MButton type="secondary" onClick={handleESSApprovals}>
            Employee Self Service
            <div className={styles.counter}>
              <div className={styles.digits}>{essListCounter}</div>
            </div>
          </MButton>
          <MButton type="secondary" onClick={handleMRApprovals}>
            Material Requisition
            <div className={styles.counter}>
              <div className={styles.digits}>{mrListCounter}</div>
            </div>
          </MButton>
        </div>
        <div className={styles.approvalsContent}>
          {activeList.map((task) => (
            <div className={styles.item} key={task.details.id}>
              <div className={styles.userInfo}>
                <div className={styles.avatar}>
                  <MAvatar size={40} />
                </div>
                <div className={styles.info}>
                  <div className={styles.userCaption}>
                    {
                      userCollection.get(task.variables?.employeeNo?.value)
                        ?.empName
                    }
                  </div>
                  <div className={styles.userOccupation}>
                    {
                      userCollection.get(task.variables?.employeeNo?.value)
                        ?.occupation
                    }
                  </div>
                  <div className={styles.userDescription}>
                    {task.variables.taskType.value}
                  </div>
                </div>
              </div>
              <div className={styles.itemContent}>
                <div className={styles.itemTime}>
                  <div className={styles.dates}>
                    <DatePickerIcon />
                    {parseDateTime(task.details.created, DATE_FORMAT)}
                  </div>
                </div>
                <div className={styles.itemActions}>
                  <MButton
                    type="primary"
                    onClick={() => {
                      handleApproveTask({
                        taskId: task.details.id,
                        requestTypeId: task.variables.taskType.value,
                        requestId: task.variables.recordId?.value,
                        requestNo: task.variables?.requestNo?.value,
                      });
                    }}
                  >
                    Approve
                  </MButton>
                  <MButton
                    danger
                    onClick={() => {
                      handleRejectTask({
                        taskId: task.details.id,
                        requestTypeId: task.variables.taskType.value,
                        requestId: task.variables.recordId?.value,
                        requestNo: task.variables?.requestNo?.value,
                      });
                    }}
                  >
                    Reject
                  </MButton>
                  <ApprovalActions
                    taskId={task.details?.id}
                    requestTypeId={task.variables.taskType?.value}
                    requestId={task.variables.recordId?.value}
                    requestNo={task.variables?.requestNo?.value}
                    requestService={
                      task.variables.taskType.value === 'ITRequisition'
                        ? task.variables.taskType.value
                        : task.variables.requestService?.value
                    }
                    handleApprovalHistory={handleApprovalHistory}
                    onApprove={() => {
                      handleApproveTask({
                        taskId: task.details.id,
                        requestTypeId: task.variables.taskType.value,
                        requestId: task.variables.recordId?.value,
                        requestNo: task.variables?.requestNo?.value,
                      });
                    }}
                    onReject={() => {
                      handleRejectTask({
                        taskId: task.details.id,
                        requestTypeId: task.variables.taskType.value,
                        requestId: task.variables.recordId?.value,
                        requestNo: task.variables?.requestNo?.value,
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default memo(ApprovalsDesktop);
